"use client";

import { motion } from "framer-motion";
import { Sprout, Droplets, Flame, PackageCheck } from "lucide-react";

const steps = [
  {
    n: "01",
    icon: Sprout,
    title: "Récolte",
    text: "Arrachage manuel au pic de maturité, respect des sols et des cycles lunaires. Chaque racine est cueillie à la main, sans blessure.",
  },
  {
    n: "02",
    icon: Droplets,
    title: "Lavage & Tri",
    text: "Brossage à l'eau claire, tri calibré racine par racine. Élimination des fibres abîmées pour ne garder que l'excellence.",
  },
  {
    n: "03",
    icon: Flame,
    title: "Transformation",
    text: "Râpage, fermentation, séchage solaire, cuisson artisanale. Les méthodes traditionnelles rencontrent l'hygiène moderne.",
  },
  {
    n: "04",
    icon: PackageCheck,
    title: "Conditionnement",
    text: "Emballage sous vide, étiquetage traçable, expédition rapide. La fraîcheur est scellée jusqu'à votre cuisine.",
  },
];

export function Transformation() {
  return (
    <section id="transformation" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background image with overlay */}
      <div className="absolute inset-0 -z-10">
        <img
          src="/images/transformation-process.png"
          alt="Atelier de transformation agroalimentaire"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/85 to-background/95" />
      </div>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left intro */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="divider-gold max-w-xs mb-5"
            >
              <span className="text-xs uppercase tracking-[0.32em] text-gold-deep font-semibold">
                Savoir-faire
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight"
            >
              L&apos;art de la{" "}
              <span className="text-gradient-gold">transformation</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 text-base sm:text-lg text-foreground/75 leading-relaxed"
            >
              Transformer une racine, c&apos;est révéler son destin. De la patate douce
              rôtie à la farine de manioc, de l&apos;attiéké fermenté aux chips
              artisanaux, notre atelier perpétue les gestes hérités tout en adoptant les
              standards d&apos;hygiène les plus exigeants. Chaque lot est tracé, chaque
              recette est notre fierté.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-8 grid grid-cols-3 gap-4"
            >
              <div className="p-4 rounded-xl glass-card text-center">
                <div className="font-display text-2xl font-extrabold text-gradient-gold">04</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                  Étapes clés
                </div>
              </div>
              <div className="p-4 rounded-xl glass-card text-center">
                <div className="font-display text-2xl font-extrabold text-gradient-gold">100%</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                  Traçabilité
                </div>
              </div>
              <div className="p-4 rounded-xl glass-card text-center">
                <div className="font-display text-2xl font-extrabold text-gradient-gold">0</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                  Additifs
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right steps */}
          <div className="lg:col-span-7">
            <div className="relative">
              {/* Vertical gold line */}
              <div className="absolute left-9 top-4 bottom-4 w-px bg-gradient-to-b from-gold via-gold/40 to-transparent" />

              <div className="space-y-8">
                {steps.map((s, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6, delay: i * 0.1 }}
                    className="relative flex gap-6"
                  >
                    <div className="relative shrink-0">
                      <div className="grid place-items-center w-20 h-20 rounded-2xl bg-card border border-gold shadow-gold">
                        <s.icon className="w-9 h-9 text-gold-deep" strokeWidth={1.8} />
                      </div>
                      <div className="absolute -top-2 -right-2 grid place-items-center w-8 h-8 rounded-full bg-gradient-gold text-white text-xs font-bold shadow-gold">
                        {s.n}
                      </div>
                    </div>

                    <div className="flex-1 pt-2 pb-4">
                      <h3 className="font-display text-2xl font-bold text-foreground">
                        {s.title}
                      </h3>
                      <p className="mt-2 text-sm sm:text-base text-foreground/75 leading-relaxed">
                        {s.text}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
